import { useState } from 'react';
import { Link } from 'react-router-dom';
import TopNav from '../components/TopNav';
import SEO from '../components/SEO';
import Footer from '../components/Footer';
import api, { getApiError } from '../lib/axios';
import {
  Search, Loader2, Globe, CheckCircle, XCircle, ExternalLink, TrendingUp, X
} from 'lucide-react';

interface SerpResult {
  domain: string;
  found_in_db: boolean;
  has_price: boolean;
  dr: number | null;
  da: number | null;
  quality_score: number | null;
  organic_traffic: number | null;
  category: string | null;
  min_price: number | null;
  seller_count: number;
}

interface SerpData {
  keyword: string;
  total_serp: number;
  matched_in_db: number;
  matched_with_price: number;
  results: SerpResult[];
}

export default function SerpScanner() {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState<SerpData | null>(null);

  const handleScan = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!keyword.trim()) return;
    setLoading(true);
    setError('');
    setData(null);
    try {
      const res = await api.get('/api/serp/scan', { params: { keyword: keyword.trim() } });
      setData(res.data);
    } catch (err: any) {
      setError(getApiError(err, 'SERP scan failed'));
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n: number | null) => {
    if (n === null || n === undefined) return '—';
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toLocaleString();
  };

  const qualityColor = (s: number) => s >= 80 ? 'bg-emerald-100 text-emerald-700' : s >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700';

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Google SERP Scanner — Find Guest Post Opportunities in Any SERP"
        description="Scan any Google SERP and find domains with guest post opportunities. See prices, quality scores, and seller availability for every ranking page."
        canonical="/serp"
      />
      <TopNav />

      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h1 className="text-3xl font-bold text-slate-900 mb-3">Find Guest Posts in Any Google SERP</h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto">
            Enter a keyword to scan Google's top results and discover which ranking sites sell guest posts.
          </p>
        </div>

        {/* Search */}
        <form onSubmit={handleScan} className="max-w-xl mx-auto mb-10">
          <div className="flex items-center border-2 border-slate-200 rounded-2xl focus-within:border-brand-500 transition-colors bg-white shadow-sm">
            <Search className="w-5 h-5 text-slate-400 ml-5" />
            <input
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
              placeholder="Enter a keyword (e.g. best CRM software, weight loss tips)"
              className="flex-1 px-4 py-4 text-base text-slate-900 placeholder-slate-400 bg-transparent outline-none"
            />
            <button type="submit" disabled={loading || !keyword.trim()}
              className="mr-2 px-6 py-2.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center gap-2">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? 'Scanning...' : 'Scan Google'}
            </button>
          </div>
        </form>

        {error && (
          <div className="max-w-xl mx-auto mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex justify-between">
            {error} <button onClick={() => setError('')}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-brand-600 mx-auto mb-3" />
            <p className="text-slate-500">Scanning Google results for "{keyword}"...</p>
            <p className="text-xs text-slate-400 mt-1">This may take a few seconds</p>
          </div>
        )}

        {/* Results */}
        {data && !loading && (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-brand-50 border border-brand-100 rounded-2xl p-6 text-center">
              <h2 className="text-xl font-bold text-slate-900 mb-2">
                {data.matched_with_price} of the top {data.total_serp} results for "<span className="text-brand-700">{data.keyword}</span>" sell guest posts
              </h2>
              <div className="flex justify-center gap-8 mt-4">
                <div>
                  <p className="text-2xl font-bold text-slate-900">{data.total_serp}</p>
                  <p className="text-xs text-slate-500">Google Results</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-brand-600">{data.matched_in_db}</p>
                  <p className="text-xs text-slate-500">In Our Database</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-emerald-600">{data.matched_with_price}</p>
                  <p className="text-xs text-slate-500">With Prices</p>
                </div>
              </div>
            </div>

            {/* Results Table */}
            <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase w-8">#</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Domain</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Quality</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Best Price</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">DR</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Traffic</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase">Sellers</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.results.map((r, idx) => (
                    <tr key={r.domain} className={`border-b border-slate-50 ${r.has_price ? 'hover:bg-emerald-50/30' : 'hover:bg-slate-50/50'}`}>
                      <td className="px-4 py-3 text-xs text-slate-400">{idx + 1}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-500 uppercase">{r.domain.charAt(0)}</div>
                          <div>
                            {r.found_in_db ? (
                              <Link to={`/domain/${r.domain}`} className="font-medium text-slate-900 hover:text-brand-600">{r.domain}</Link>
                            ) : (
                              <span className="font-medium text-slate-900">{r.domain}</span>
                            )}
                            {r.category && <p className="text-xs text-slate-400">{r.category}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        {r.has_price ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Sells GP
                          </span>
                        ) : r.found_in_db ? (
                          <span className="px-2 py-0.5 bg-slate-100 text-slate-500 rounded text-xs">In DB</span>
                        ) : (
                          <span className="px-2 py-0.5 bg-slate-50 text-slate-400 rounded text-xs">Not found</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {r.quality_score ? (
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${qualityColor(r.quality_score)}`}>{r.quality_score}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3">
                        {r.min_price !== null ? (
                          <span className="font-bold text-emerald-600">${r.min_price.toFixed(0)}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-700">{r.dr ?? '—'}</td>
                      <td className="px-4 py-3 text-slate-600">{fmt(r.organic_traffic)}</td>
                      <td className="px-4 py-3">
                        {r.seller_count > 0 ? (
                          <span className="px-2 py-0.5 bg-slate-100 rounded text-xs font-medium">{r.seller_count}</span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {r.has_price && (
                          <Link to={`/domain/${r.domain}`} className="text-brand-600 hover:underline text-xs font-medium">
                            View →
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {data.results.length === 0 && (
              <div className="text-center py-12">
                <Globe className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No results from Google for this keyword.</p>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!data && !loading && (
          <div className="text-center py-8">
            <div className="grid md:grid-cols-3 gap-6 max-w-3xl mx-auto mt-8">
              {[
                { title: 'Find link opportunities', desc: 'See which sites ranking for your target keywords accept guest posts.' },
                { title: 'Compare prices instantly', desc: 'Know exactly how much it costs to get a link from a top-ranking page.' },
                { title: 'Outrank competitors', desc: 'Build links on the same sites your competitors rank on — at the best price.' },
              ].map(item => (
                <div key={item.title} className="p-5 border border-slate-200 rounded-2xl text-left">
                  <h3 className="font-semibold text-slate-900 mb-1 text-sm">{item.title}</h3>
                  <p className="text-xs text-slate-500">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
